/**
 * lib/api.js (r6) — the JSON client the Phase 9/10 pages were written against.
 * 28 components import `{ api } from '../../lib/api'`; the module never existed, so the web build failed.
 *
 * Contract (as those call sites use it):
 *   api.get('/api/notify-me')            → parsed JSON body (NOT an axios response)
 *   api.post('/api/bookings/festival', body) → parsed JSON body
 *   api.delete(url, body?)               → parsed JSON body (body allowed — NotifyMeBell sends { show_id })
 *   errors: throw Error with .status (HTTP status) and .code/.message from the platform envelope { error, message }
 *
 * Paths may be given as '/api/x' (gateway-facing, as the pages do) or '/x'; both resolve against the shared axios
 * instance (baseURL VITE_API_BASE, default '/api') so auth headers, refresh-on-401 and retries are inherited.
 */
import client from '../api/client';

const BASE = (import.meta.env.VITE_API_BASE || '/api').replace(/\/$/, '');

function normalise(url) {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith(BASE + '/') || url === BASE) return url.slice(BASE.length) || '/';
  return url;
}

function toError(err) {
  const status = err?.response?.status || err?.status || 0;
  const body = err?.response?.data || {};
  const e = new Error(body.message || body.error || err?.message || `HTTP ${status}`);
  e.status = status;
  e.code = body.error || body.code || (status === 0 ? 'NETWORK_ERROR' : 'HTTP_ERROR');
  e.details = body.details;
  e.request_id = body.request_id;
  return e;
}

async function call(method, url, body, config = {}) {
  try {
    const r = await client.request({ method, url: normalise(url), data: body, ...config });
    return r.data;
  } catch (err) {
    const e = toError(err);
    // r6 waiting room: gateway answers 429 QUEUED on hot routes → park the user on /queue, come back when admitted
    if (e.status === 429 && err?.response?.data?.code === 'QUEUED' && typeof window !== 'undefined' && !location.pathname.startsWith('/queue')) {
      window.location.assign(`/queue?return=${encodeURIComponent(location.pathname + location.search)}`);
    }
    throw e;
  }
}

export const api = {
  get: (url, config) => call('get', url, undefined, config),
  post: (url, body, config) => call('post', url, body, config),
  put: (url, body, config) => call('put', url, body, config),
  patch: (url, body, config) => call('patch', url, body, config),
  delete: (url, body, config) => call('delete', url, body, config),
  raw: client,
};

export default api;
